import React from "react";
import moment from "moment";
import { Table as SharedTable } from "../../../../../../shared/Table/Table";
import { useAppTranslation } from "../../../../../../i18n/useAppTranslation";
import { Skeleton } from "../../../../../../shared/Skeleton/Skeleton";
import { Empty } from "../../../../../../shared/Empty/Empty";
import { SortAdornment } from "../../../../../../shared/SortAdornment/SortAdornment";
import { ContentLoader } from "../../../../../../boxes/ContentLoader/ContentLoader";
import { LoadMoreObserver } from "../../../../../../shared/LoadMoreObserver/LoadMoreObserver";
import { useCurrentUserQuery } from "../../../../../../api/queries/useUserQuery";
import {
  DEFAULT_DATE_FORMAT,
  DEFAULT_TIME_FORMAT,
} from "../../../../../../consts/general";
import { ORDER_BY } from "../../../../../../api/patientDocuments/usePatientDocumentsQuery";
import styles from "./styles.module.scss";
import { Checkbox } from "../../../../../../shared/Checkbox/Checkbox";
import { useActionsRenderer } from "./hooks/useActionsRenderer";
import { Actions } from "./shared/Actions";
import GFEIconV2 from "../../../../../../assets/Icons/GFEIconV2";
import { EyeSlashIcon } from "../../../../../../assets/Icons/EyeSlashIcon";
import { isDocumentSelectable } from "../../utilities";

/**
 * @param {{
 *  documents: {
 *    value: Array<{
 *      id: number;
 *      name: string;
 *      isProviderQuestionnaire: boolean;
 *      type: string;
 *      date: string;
 *      expiry: string;
 *      editor: string;
 *      isLock: boolean;
 *      expireSoon: boolean;
 *      url: string;
 *      acknowledgedAt?: string;
 *      canEdit: boolean;
 *    }>;
 *    isLoading: boolean;
 *    isSoftLoading: boolean;
 *    fetchMore: function;
 *    hasMore: boolean;
 *  };
 *  order: {
 *    value: {
 *      by: string;
 *      direction: string;
 *    };
 *    update: function;
 *  };
 *  selection: {
 *    isSelectedAll: function;
 *    isSelected: function;
 *    onToggleAll: function;
 *    onToggleOne: function;
 *  }
 *  permissions: {
 *    canMutate: boolean;
 *    canView: boolean;
 *    values: string[];
 *  };
 * }} param0
 */
export function Table({ documents, order, selection, permissions }) {
  var { tClients: t } = useAppTranslation.Clients();
  var { data: user } = useCurrentUserQuery();
  var renderActions = useActionsRenderer({
    refetchDocuments: documents.refetch,
  });

  var isEmpty = documents.value.length === 0 && !documents.isLoading;
  var isVisible = !documents.isLoading && !isEmpty;

  var renderSortWrap = (children, field) => (
    <SortAdornment
      value={order.value.by === field ? order.value.direction : undefined}
      onToggle={() => order.update(field)}
    >
      {children}
    </SortAdornment>
  );

  return (
    <div>
      {documents.isLoading && !documents.isSoftLoading && (
        <Skeleton count={15} height={40.5} borderRadius="0px" />
      )}
      {isEmpty && <Empty position="center" />}
      {isVisible && (
        <ContentLoader
          className={styles.wrapper}
          isLoading={documents.isSoftLoading}
        >
          <SharedTable
            bgColor="white"
            data={documents.value}
            keyExtractor={(x) => x.id}
            getRowClassName={(row) =>
              row.isExpired ? styles.expiredDocument : undefined
            }
            cols={[
              {
                data:
                  permissions.canMutate || permissions.canView ? (
                    <Checkbox
                      inverted
                      size="small"
                      isChecked={selection.isSelectedAll()}
                      onChange={() => selection.onToggleAll()}
                    />
                  ) : null,
                accessor: "id",
                dataRenderer: (id, row) =>
                  isDocumentSelectable(row, {
                    canMutate: permissions.canMutate,
                    canView: permissions.canView,
                    permissions: permissions.values,
                  }) ? (
                    <Checkbox
                      size="small"
                      isChecked={selection.isSelected(id)}
                      onChange={() => selection.onToggleOne(id)}
                    />
                  ) : null,
                className: styles.colCheckbox,
              },
              {
                data: renderSortWrap(t("documents.table.name"), ORDER_BY.name),
                accessor: "name",
                dataRenderer: (value, row) => (
                  <div className={styles.name}>
                    <span>{value}</span>
                    {row.isProviderQuestionnaire && (
                      <EyeSlashIcon
                        width="30px"
                        style={{
                          minWidth: "30px",
                          color: "red",
                          marginTop: "2px",
                        }}
                      />
                    )}
                  </div>
                ),
                className: styles.colLarge,
              },
              {
                data: renderSortWrap(t("documents.table.type"), ORDER_BY.type),
                accessor: "type",
                dataRenderer: (value, row) => (
                  <div className={styles.type}>
                    <span>{value}</span>
                    {Boolean(row.isGfe) && (
                      <GFEIconV2 color={row.gfe} width="40px" height="30px" />
                    )}
                  </div>
                ),
                className: styles.colLarge,
              },
              {
                data: renderSortWrap(t("documents.table.date"), ORDER_BY.date),
                accessor: "date",
                dataRenderer: (value) =>
                  moment(value).format(
                    `${user?.dateFormat || DEFAULT_DATE_FORMAT} ${
                      user?.timeFormat || DEFAULT_TIME_FORMAT
                    }`,
                  ),
                className: styles.colLarge,
              },
              {
                data: renderSortWrap(
                  t("documents.table.expiry"),
                  ORDER_BY.expiry,
                ),
                accessor: "expiry",
                dataRenderer: (value) =>
                  value
                    ? moment(value).format(
                        user?.dateFormat || DEFAULT_DATE_FORMAT,
                      )
                    : "N/A",
                className: styles.col,
              },
              {
                data: t("documents.table.editor"),
                accessor: "editor",
                className: styles.colLarge,
              },
              {
                data: t("documents.table.actions"),
                accessor: "actions",
                dataRenderer: (_, row) => {
                  return (
                    <div className={styles.actions}>
                      {renderActions(row, Actions)}
                    </div>
                  );
                },
              },
            ]}
          />
          {documents.hasMore && (
            <LoadMoreObserver
              noPaddingBottom
              loadMore={documents.fetchMore}
              isLoading={documents.isNextFetching}
            />
          )}
        </ContentLoader>
      )}
    </div>
  );
}
